/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';
import {
  clearGraphModelError,
  getErrorMessage,
  handleGraphServiceHttpError,
  setNotification,
  unwrapServiceData
} from './graphServiceUtils';
import { buildRubricsCreatePath } from './rubricsApiPaths';

/** POST /graphs/:graphId/evaluations */
export default class CreateEvaluationService extends BaseService {
  constructor() {
    super(constants.CREATE_EVALUATION, 'createEvaluationStatus');
  }

  buildSavedRubricFromResponse(data) {
    const body = data?.data != null && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : data;
    return {
      evalRubric: body?.evalRubric ?? body?.rubricText ?? '',
      rubricName: body?.rubricName ?? '',
      evalTypes: body?.evalTypes,
      ...(body?.rubricId != null && body?.rubricId !== '' ? { rubricId: String(body.rubricId) } : {}),
      ...(body?.rubricVersion != null ? { rubricVersion: body.rubricVersion } : {})
    };
  }

  publishSaveError(data, msg) {
    setNotification({
      type: 'error',
      message: msg
    });
    Framework.getStoreRegistry().getStore('graphModel').setError(msg);
    Framework.getEventManager().publish(constants.CREATE_EVALUATION_ERROR, { data, message: msg });
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { graphId, requestBody } = payload;
    const url = buildRubricsCreatePath(graphId);

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').post(url, requestBody, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').post(url, requestBody, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      clearGraphModelError();
      setNotification(
        data?.message
          ? { type: 'success', message: data.message }
          : { type: 'success', langMessage: 'graphEvaluation.SAVE_RUBRIC_TEXT_SUCCESS' }
      );
      const savedRubric = this.buildSavedRubricFromResponse(unwrapServiceData(response) ?? data);
      const eventPayload = { data, ...savedRubric, isCreate: true };
      Framework.getEventManager().publish(constants.CREATE_EVALUATION_SUCCESS, eventPayload);
      Framework.getEventManager().publish(constants.SAVE_EVALUATION_RUBRIC_TEXT_SUCCESS, eventPayload);
      return;
    }

    const msg = getErrorMessage(data, 'Unknown error');
    this.publishSaveError(data, msg);
  }

  handleError(error) {
    handleGraphServiceHttpError(error, {
      fallbackLangMessage: 'graphEvaluation.SAVE_RUBRIC_TEXT_RETRY',
      errorEvent: constants.CREATE_EVALUATION_ERROR,
      updateGraphModelError: true
    });
  }
}
